/**
 * 资产-音频管理器
 * 负责管理资产-音频列表
 * @author Joey
 */
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { assetsVoiceList } from "@/services/assets-voice";
import { voiceRename } from "@/services/audio-rename-template";
import { jsonStore } from "@/manager/cache/localdb";
import { agentManager } from "./agent-manager";

type VoiceEventParams = {
  voices: Audio[];
};

type VoiceEvent = CustomEvent<VoiceEventParams>;

class AssetsVoiceEvents {
  constructor(private eventName: string) {}

  listen(listener: (e: VoiceEvent) => void): () => void {
    window.addEventListener(this.eventName, listener);
    return () => {
      window.removeEventListener(this.eventName, listener);
    };
  }

  dispatch(detail: VoiceEventParams): void {
    const event = new CustomEvent<VoiceEventParams>(this.eventName, { detail });
    window.dispatchEvent(event);
  }
}

interface AssetsVoiceCacheData {
  voices: Audio[];
  page: number;
  size: number;
  total: number;
}

class AssetsVoiceLocal {
  constructor(private storageKey: string) {}

  async get(): Promise<AssetsVoiceCacheData | null> {
    return await jsonStore.get<AssetsVoiceCacheData>(this.storageKey);
  }

  async set(data: AssetsVoiceCacheData): Promise<void> {
    await jsonStore.set(this.storageKey, data);
  }

  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AssetsVoiceRemote {
  constructor(private cache: AssetsVoiceLocal) {}

  async getCached() {
    return await this.cache.get();
  }

  async fetchVoices(page: number, pageSize: number) {
    const data = await assetsVoiceList({ page, pageSize });
    const voices = data.templates
      ? data.templates.map((item) => ({
          id: item.id,
          voiceTtsId: item.voiceTtsId,
          name: item.name,
          modelName: item.provider,
          ageGroup: "",
          language: "",
          gender: "",
          emotion: "",
          speed: 1,
          isStarred: false,
          audioUrl: item.sampleUrl,
          createdAt: item.createdAt,
        }))
      : [];
    // 请求成功后更新缓存
    await this.cache.set({
      voices,
      page: data.page,
      size: data.pageSize,
      total: data.total,
    });
    return {
      voices,
      page: data.page,
      pageSize: data.pageSize,
      total: data.total,
    };
  }

  async fetchUserVoices(userId: string | undefined, page: number, pageSize: number) {
    const data = await assetsVoiceList({ page, pageSize, userId });
    const voices = data.templates
      ? data.templates.map((item) => ({
          id: item.id,
          voiceTtsId: item.voiceTtsId,
          name: item.name,
          modelName: item.provider,
          ageGroup: "",
          language: "",
          gender: "",
          emotion: "",
          speed: 1,
          isStarred: false,
          audioUrl: item.sampleUrl,
          createdAt: item.createdAt,
        }))
      : [];
    return voices;
  }

  async renameVoice(id: string, name: string) {
    return voiceRename({ id, name });
  }
}

export class AssetsVoiceManager {
  private readonly STORAGE_KEY = "MIUTA_ASSETS_VOICE";
  private activeStorageKey = "";
  private voices: Audio[] = [];
  private page: number = 1;
  private size: number = 1000;

  private cache: AssetsVoiceLocal;
  private api: AssetsVoiceRemote;
  private events: AssetsVoiceEvents;

  constructor() {
    this.cache = new AssetsVoiceLocal(this.getStorageKey());
    this.api = new AssetsVoiceRemote(this.cache);
    this.events = new AssetsVoiceEvents(this.getStorageKey());
    this.activeStorageKey = this.getStorageKey();
  }

  public getVoices(): Audio[] {
    return this.voices;
  }

  private getStorageKey() {
    const agentId = agentManager.getAgentId() || "";
    return `${this.STORAGE_KEY}:${agentId}`;
  }

  private ensureStorageScope() {
    const nextStorageKey = this.getStorageKey();
    if (nextStorageKey === this.activeStorageKey) {
      return;
    }
    this.activeStorageKey = nextStorageKey;
    this.cache = new AssetsVoiceLocal(nextStorageKey);
    this.api = new AssetsVoiceRemote(this.cache);
    this.events = new AssetsVoiceEvents(nextStorageKey);
  }

  private setVoices(voices: Audio[]) {
    this.ensureStorageScope();
    this.voices = voices;
    this.dispatch();
  }

  public async init() {
    this.ensureStorageScope();
    try {
      const cached = await this.api.getCached();
      if (cached) {
        this.setVoices(cached.voices);
        this.page = cached.page;
        this.size = cached.size;
        // 后台同步数据
        this.fetch();
        return;
      }
    } catch (error) {
      console.error("get cache data failed", error);
      this.setVoices([]);
      this.page = 1;
      this.size = 1000;
    }
    // 无缓存需要等待数据请求返回
    await this.fetch();
  }

  public async fetch() {
    this.ensureStorageScope();
    const data = await this.api.fetchVoices(this.page, this.size);
    // 数据始终以后端返回为准
    this.setVoices(data.voices);
    this.page = data.page;
    this.size = data.pageSize;
  }

  // 获取指定用户音频列表
  public async fetchUserVoices(userId?: string) {
    this.ensureStorageScope();
    try {
      const voices = await this.api.fetchUserVoices(userId, this.page, this.size);
      return voices;
    } catch (error) {
      console.error("get user voices failed", error);
      return [];
    }
  }

  public addVoice(voice: Audio): void {
    this.setVoices([voice, ...this.voices]);
  }

  public async voiceRename({ audio, name }: { audio: Audio, name: string }) {
    // 用于回退
    const originVoices = [...this.voices];
    try {
      // 乐观更新
      const voices = this.voices.map((item) => {
        if (item.id === audio.id) {
          return {
            ...item,
            name,
          };
        }
        return item;
      });
      this.setVoices(voices);
      const res = await this.api.renameVoice(audio.id, name);
      if (res.isDuplicate) {
        // 回退更新
        this.setVoices(originVoices);
      }
      // 继续返回结果
      return res;
    } catch (error) {
      // 回退更新
      this.setVoices(originVoices);
      // 继续抛出异常
      throw error;
    }
  }

  // 重置参数到默认值
  public reset(): void {
    this.setVoices([]);
    this.page = 1;
    this.size = 1000;
    this.clear();
  }

  // 清除所有缓存
  public clear(): void {
    this.ensureStorageScope();
    this.cache.clear();
  }

  public listen(listener: (e: VoiceEvent) => void): () => void {
    return this.events.listen(listener);
  }

  private dispatch(): void {
    this.events.dispatch({ voices: this.voices });
  }
}

export const useAssetsVoiceData = () => {
  const [voices, setVoices] = useState(assetsVoiceManager.getVoices());
  const [loading, setLoading] = useState(false);
  const { showToastByCode } = useToast();

  useEffect(() => {
    return assetsVoiceManager.listen((e) => {
      setVoices(e.detail.voices);
    });
  }, []);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      await assetsVoiceManager.init();
    } catch (error) {
      showToastByCode(error);
    }
    setLoading(false);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await assetsVoiceManager.fetch();
    } catch (error) {
      showToastByCode(error);
    }
    setLoading(false);
  };

  return {
    voices,
    loading,
    refresh,
  };
};

export const useAssetsVoiceRename = () => {
  const { error, showToastByCode } = useToast();

  const renameVoice = async ({ audio, name }: { audio: Audio, name: string }) => {
    try {
      const res = await assetsVoiceManager.voiceRename({ audio, name });
      // 重名检查
      if (res.isDuplicate) {
        error({ description: 'Voice name already exists' });
      }
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    }
  };

  return {
    renameVoice,
  };
};

export const useAssetsVoiceAdd = () => {
  const addVoice = (voice: Audio) => {
    assetsVoiceManager.addVoice(voice);
  };

  return {
    addVoice,
  };
};

export const assetsVoiceHooks = {
  useData: useAssetsVoiceData,
  useRename: useAssetsVoiceRename,
  useAdd: useAssetsVoiceAdd,
};

// 导出单例实例
export const assetsVoiceManager = new AssetsVoiceManager();